import axios from 'axios'
import React, { useEffect, useState } from 'react'
import {
  Container,
  Card,
  Table,
  Button,
  Row,
  Col,
  Form,
  Modal,
  Spinner,
} from 'react-bootstrap'
import toast from 'react-hot-toast'
import { Link, useNavigate } from 'react-router-dom'
import Cookies from 'js-cookie'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import AddTransactionModal from './AddTransactionModal'
import {theme} from '../theme/theme' // <-- Your theme.js file
import CustomBtn from '../common/CustomBtn'
import { BASE_URL } from '../constants'


const token = Cookies.get('authToken')
const axiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  },
})

const CDateRangePicker = ({ startDate, endDate, onStartDateChange, onEndDateChange }) => (
  <Row className="g-2">
    <Col>
      <DatePicker
        selected={startDate}
        onChange={onStartDateChange}
        selectsStart
        startDate={startDate}
        endDate={endDate}
        dateFormat="MM/dd/yyyy"
        placeholderText="Start Date"
        className="form-control"
      />
    </Col>
    <Col>
      <DatePicker
        selected={endDate}
        onChange={onEndDateChange}
        selectsEnd
        startDate={startDate}
        endDate={endDate}
        dateFormat="MM/dd/yyyy"
        placeholderText="End Date"
        className="form-control"
      />
    </Col>
  </Row>
)

const BookingDetailPage = () => {
  const [allBookings, setAllBookings] = useState([])
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [selectedBookingId, setSelectedBookingId] = useState(null)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [proceedModalVisible, setProceedModalVisible] = useState(false)
  const [backendMessage, setBackendMessage] = useState('')
  const [showFilterModal, setFilterModal] = useState(false)
  const [customerName, setCustomerName] = useState('')
  const [customerPhone, setCustomerPhone] = useState('')
  const [userType, setUserType] = useState()
  const [isHovered, setIsHovered] = useState(null)

  const navigate = useNavigate()

  useEffect(() => {
    const role = Cookies.get('user_type')
    setUserType(role || 'EMPLOYEE')
  }, [])

  const fetchBookings = async (start_date, end_date) => {
    setLoading(true)
    try {
      const bookingsUrl = start_date && end_date ? `get/bookings/date?start_date=${start_date}&end_date=${end_date}` : `get/bookings/date`
      const bookingResponse = await axiosInstance.get(bookingsUrl)
      if (!bookingResponse.data?.data?.length) {
        setAllBookings([])
        return
      }

      const bookings = bookingResponse.data.data
      const [customerRes, brokerRes, plotRes, siteRes] = await Promise.all([
        axiosInstance.get('customer/search'),
        axiosInstance.get('brokers/search'),
        axiosInstance.get('plot/search'),
        axiosInstance.get('sites'),
      ])

      const customers = customerRes.data.data
      const brokers = brokerRes.data.data
      const plots = plotRes.data.data
      const sites = siteRes.data.data

      const enrichedBookings = bookings.map((b) => {
        const customer = customers.find((c) => c.id === b.customer_id) || {}
        const broker = brokers.find((br) => br.broker_id === b.broker_id) || {}
        const plotData = plots.find((p) => p.id === b.plot_id) || {}
        const site = sites.find((s) => s.id === plotData.site_id) || {}

        return {
          ...b,
          customer_name: customer.name || b.customer_name,
          customer_title: customer.title || b.customer_title,
          broker_name: broker.name || 'Not assigned',
          plot_number: plotData.plot_number || b.plot_number,
          site_name: site.name || plotData.site_name || '',
        }
      })

      setAllBookings(enrichedBookings)
    } catch (error) {
      console.error(error)
      toast.error(error.response?.data?.message || 'Failed to fetch bookings')
    } finally {
      setLoading(false)
    }
  }

  const handleFetchData = () => {
    if (!startDate) return
    const sDate = new Date(startDate)
    sDate.setDate(sDate.getDate() + 1)
    const formattedStart = sDate.toISOString().split('T')[0]

    let formattedEnd = formattedStart
    if (endDate) {
      const eDate = new Date(endDate)
      eDate.setDate(eDate.getDate() + 1)
      formattedEnd = eDate.toISOString().split('T')[0]
    }
    fetchBookings(formattedStart, formattedEnd)
  }

  const handleCancelBooking = async () => {
    setShowCancelModal(false)
    const token = Cookies.get('authToken')
    if (!token) {
      setBackendMessage('Authentication token not found')
      setProceedModalVisible(true)
      return
    }

    try {
      const response = await axios.delete(BASE_URL+`/booking/cancel`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { booking_id: selectedBookingId },
      })

      setBackendMessage(response.data?.message || 'Booking canceled successfully')
      fetchBookings()
    } catch (error) {
      console.error(error)
      setBackendMessage(error.response?.data?.message || 'Failed to cancel booking')
    }
    setProceedModalVisible(true)
  }

  const handleSearch = async () => {
    if (!customerName && !customerPhone) {
      toast.error('Please provide customer name or phone number')
      return
    }
    setLoading(true)
    try {
      let query = ''
      if (customerName) query += `name=${encodeURIComponent(customerName)}&`
      if (customerPhone) query += `phone_number=${encodeURIComponent(customerPhone)}&`
      query = query.slice(0, -1)

      const res = await axios.get(BASE_URL+`/search/bookings?${query}`, {
        headers: { Authorization: `Bearer ${Cookies.get('authToken')}` },
      })

      if (!res.data?.data?.length) {
        setAllBookings([])
        return
      }

      const bookings = res.data.data
      const [customerRes, brokerRes, plotRes, siteRes] = await Promise.all([
        axiosInstance.get('customer/search'),
        axiosInstance.get('brokers/search'),
        axiosInstance.get('plot/search'),
        axiosInstance.get('sites'),
      ])
      const customers = customerRes.data.data
      const brokers = brokerRes.data.data
      const plots = plotRes.data.data
      const sites = siteRes.data.data

      const enrichedBookings = bookings.map((b) => {
        const customer = customers.find((c) => c.id === b.customer_id) || {}
        const broker = brokers.find((br) => br.broker_id === b.broker_id) || {}
        const plotData = plots.find((p) => p.id === b.plot_id) || {}
        const site = sites.find((s) => s.id === plotData.site_id) || {}
        return {
          ...b,
          customer_name: customer.name || b.customer_name,
          customer_title: customer.title || b.customer_title,
          broker_name: broker.name || 'Not assigned',
          plot_number: plotData.plot_number || b.plot_number,
          site_name: site.name || plotData.site_name || '',
        }
      })

      setAllBookings(enrichedBookings)
    } catch (error) {
      console.error(error)
      toast.error('Failed to fetch booking data')
    } finally {
      setLoading(false)
      setFilterModal(false)
    }
  }

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })

  useEffect(() => {
    const token = Cookies.get('authToken')
    if (!token) window.location.href = '/login'
    else fetchBookings()
  }, [])

  return (
    <>
      {/* Cancel Booking Modal */}
      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ color: theme.colors.error }}>Cancel Booking ID: {selectedBookingId}?</Modal.Title>
        </Modal.Header>
        <Modal.Body>Once canceled, this booking will no longer be available.</Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button style={{ backgroundColor: theme.colors.error, borderColor: theme.colors.error }} onClick={handleCancelBooking}>Proceed</Button>
        </Modal.Footer>
      </Modal>

      {/* Message Modal */}
      <Modal show={proceedModalVisible} onHide={() => setProceedModalVisible(false)} centered>
        <Modal.Body className="text-center">{backendMessage}</Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button style={{ backgroundColor: theme.colors.success, borderColor: theme.colors.success }} onClick={() => setProceedModalVisible(false)}>OK</Button>
        </Modal.Footer>
      </Modal>

      <Container className="pb-3" style={{fontFamily: theme.font.family }}>
      
        <div className='rounded-3'>
          {/* <Card.Header className="text-center" style={{ color: theme.colors.primary, fontWeight: 'bold' }}>
            <h2>All Bookings</h2>
          </Card.Header> */}

          {/* Filters */}
          <div className='px-3 mb-3' style={{ backgroundColor: theme.colors.background }}>
            
            <Row className="p-2 align-items-end">
            <Col xs={12} md={6} lg={4}>
              <Form.Label style={{ fontWeight: 'bold', color: theme.colors.primary }}>Select Booking Date</Form.Label>
              <CDateRangePicker startDate={startDate} endDate={endDate} onStartDateChange={setStartDate} onEndDateChange={setEndDate} />
            </Col>
            <Col className="mt-2">
              {/* <Button style={{ backgroundColor: theme.colors.secondary, borderColor: theme.colors.secondary }} onClick={handleFetchData}>Search</Button> */}
              <CustomBtn onClick={handleFetchData}>Search</CustomBtn>
            </Col>
            <Col className="text-end mt-2">
              <Button style={{ backgroundColor: theme.colors.warning, borderColor: theme.colors.warning }} onClick={() => setFilterModal(true)}>🔍 More Filter</Button>
            </Col>
          </Row>
            
            
          </div>


          {/* Filter Modal */}
          <Modal show={showFilterModal} onHide={() => setFilterModal(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title style={{ color: theme.colors.primary }}>Search Filters</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form>
                <Form.Group className="mb-2">
                  <Form.Label>Customer Name</Form.Label>
                  <Form.Control type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Enter customer name" />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Customer Phone Number</Form.Label>
                  <Form.Control type="text" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="Enter phone number" />
                </Form.Group>
              </Form>
            </Modal.Body>
            <Modal.Footer className="justify-content-center">
              <Button style={{ backgroundColor: theme.colors.error, borderColor: theme.colors.error }} onClick={() => setFilterModal(false)}>Close</Button>
              <Button style={{ backgroundColor: theme.colors.primary, borderColor: theme.colors.primary }} onClick={handleSearch}>Search</Button>
            </Modal.Footer>
          </Modal>

          {/* Bookings Table */}
          {/* <Card.Body> */}
          <div className='px-3 mb-3' style={{ backgroundColor: theme.colors.background , }}>
            <Table bordered hover responsive className="text-center">
              <thead style={{ color: '#fff', position: 'sticky', top: 0 }}>
                  <tr className="text-start">
                    <th style={{ backgroundColor: `${theme.colors.primary}`, color: theme.colors.background }}>Booking ID</th>
                    <th style={{ backgroundColor: `${theme.colors.primary}`, color: theme.colors.background }}>Date</th>
                    <th style={{ backgroundColor: `${theme.colors.primary}`, color: theme.colors.background }}>Status</th>
                    <th style={{ backgroundColor: `${theme.colors.primary}`, color: theme.colors.background }}>Amount</th>
                    <th style={{ backgroundColor: `${theme.colors.primary}`, color: theme.colors.background }}>Customer</th>
                    <th style={{ backgroundColor: `${theme.colors.primary}`, color: theme.colors.background }}>Associate</th>
                    <th style={{ backgroundColor: `${theme.colors.primary}`, color: theme.colors.background }}>Site</th>
                    <th style={{ backgroundColor: `${theme.colors.primary}`, color: theme.colors.background }}>Plot</th>
                    <th style={{ backgroundColor: `${theme.colors.primary}`, color: theme.colors.background }}>Print</th>
                    <th style={{ backgroundColor: `${theme.colors.primary}`, color: theme.colors.background }}>Actions</th>
                  </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={10} className="text-center"><Spinner animation="border" /></td>
                  </tr>
                ) : allBookings.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-danger">No bookings found</td>
                  </tr>
                ) : allBookings.map((b) => (
                  <tr key={b.id}>
                    <td><Link to={`/BookedDetailpage/${b.id}`}>{b.id}</Link></td>
                    <td>{formatDate(b.booking_date)}</td>
                    <td>{b.booking_status}</td>
                    <td>₹ {b.booking_price}</td>
                    <td>{`${b.customer_title}. ${b.customer_name}`}</td>
                    <td>{b.broker_name}</td>
                    <td>{b.site_name}</td>
                    <td>{b.plot_number}</td>
                    <td><Link to={`/BookingPrintpage/${b.id}?source=print`}>Click</Link></td>
                    <td className="d-flex flex-wrap justify-content-center gap-1">
                      <Button variant="outline-success" size="sm" title="Add Transaction" onClick={() => { setSelectedBookingId(b.id); setShowModal(true) }}>
                        <i className="fas fa-plus-circle"></i>
                      </Button>
                      <Button variant="outline-primary" size="sm" title="See Transactions" onClick={() => navigate(`/seetransaction/${b.id}`)}>
                        <i className="fas fa-eye"></i>
                      </Button>
                      {userType === 'ADMIN' && (
                        <Button variant="outline-info" size="sm" title="Change Plot" onClick={() => navigate(`/changeplot/${b.id}`, { state: { booking: b } })}>
                          <i className="fas fa-pen-to-square"></i>
                        </Button>
                      )}
                      <Button variant="outline-danger" size="sm" title="Cancel Booking" onClick={() => { setSelectedBookingId(b.id); setShowCancelModal(true) }}>
                        <i class="fa-solid fa-trash"></i>

                          

                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <AddTransactionModal
              show={showModal}
              handleClose={() => setShowModal(false)}
              handleSubmit={() => {}}
              bookingId={selectedBookingId}
            />
          </div>

          {/* </Card.Body> */}
        </div>
      </Container>
    </>
  )
}

export default BookingDetailPage
